import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { useLegacyLanguageRedirect } from "@/hooks/useLegacyLanguageRedirect";

// Helper to handle chunk loading failures (stale cache after deployment)
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(() =>
    componentImport().catch(() => {
      window.location.reload();
      return componentImport();
    })
  );

// Lazy-load toast component (only needed when toasts are triggered)
const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));

// Critical page - loaded immediately for best FCP/LCP
import Index from "./pages/Index";

// Lazy load all Portuguese pages
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const RegistoMotorista = lazyWithRetry(() => import("./pages/RegistoMotorista"));
const RequisitosMotoristaYango = lazyWithRetry(() => import("./pages/RequisitosMotoristaYango"));
const CadastroMotorista = lazyWithRetry(() => import("./pages/CadastroMotorista"));
const BaixarYangoProMaputo = lazyWithRetry(() => import("./pages/BaixarYangoProMaputo"));
const RecargaAutomaticaYangoPro = lazyWithRetry(() => import("./pages/RecargaAutomaticaYangoPro"));
const Documentation = lazyWithRetry(() => import("./pages/Documentation"));
const SobreNos = lazyWithRetry(() => import("./pages/SobreNos"));
const TermosDeUso = lazyWithRetry(() => import("./pages/TermosDeUso"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

// English pages (dedicated /en/ routes)
const EnIndex = lazyWithRetry(() => import("./pages/en/Index"));
const EnRegistoMotorista = lazyWithRetry(() => import("./pages/en/RegistoMotorista"));
const EnCadastroMotorista = lazyWithRetry(() => import("./pages/en/CadastroMotorista"));
const EnRequisitosMotoristaYango = lazyWithRetry(() => import("./pages/en/RequisitosMotoristaYango"));
const EnBaixarYangoProMaputo = lazyWithRetry(() => import("./pages/en/BaixarYangoProMaputo"));
const EnRecargaAutomaticaYangoPro = lazyWithRetry(() => import("./pages/en/RecargaAutomaticaYangoPro"));
const EnBlog = lazyWithRetry(() => import("./pages/en/Blog"));
const EnBlogPost = lazyWithRetry(() => import("./pages/en/BlogPost"));
const EnAboutUs = lazyWithRetry(() => import("./pages/en/AboutUs"));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-primary text-lg">Loading...</div>
  </div>
);

// Wrapper component to call hook inside Router context
const LegacyRedirectHandler = () => {
  useLegacyLanguageRedirect();
  return null;
};

const App = () => (
  <BrowserRouter>
    <LanguageProvider>
      <LegacyRedirectHandler />
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Portuguese routes (default) */}
          <Route path="/" element={<Index />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/registo-yango-motorista-maputo" element={<RegistoMotorista />} />
          <Route path="/requisitos-motorista-yango" element={<RequisitosMotoristaYango />} />
          <Route path="/cadastro-motorista-yango" element={<CadastroMotorista />} />
          <Route path="/baixar-yango-pro-maputo" element={<BaixarYangoProMaputo />} />
          <Route path="/recarga-automatica-yango-pro-emola-mpesa" element={<RecargaAutomaticaYangoPro />} />
          <Route path="/documentacao" element={<Documentation />} />
          <Route path="/sobre-nos" element={<SobreNos />} />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          
          {/* English routes (dedicated /en/ prefix) */}
          <Route path="/en/" element={<EnIndex />} />
          <Route path="/en/yango-driver-registration-maputo" element={<EnRegistoMotorista />} />
          <Route path="/en/yango-driver-signup" element={<EnCadastroMotorista />} />
          <Route path="/en/yango-driver-requirements" element={<EnRequisitosMotoristaYango />} />
          <Route path="/en/download-yango-pro-maputo" element={<EnBaixarYangoProMaputo />} />
          <Route path="/en/automatic-top-up-yango-pro" element={<EnRecargaAutomaticaYangoPro />} />
          <Route path="/en/blog" element={<EnBlog />} />
          <Route path="/en/blog/:slug" element={<EnBlogPost />} />
          <Route path="/en/about-us" element={<EnAboutUs />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </LanguageProvider>
  </BrowserRouter>
);

export default App;